import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import ScrollToTop from "@/components/shared/ScrollToTop";
import { I18nProvider } from "@/contexts/I18nContext";
import Index from "./pages/Index.tsx";
import LogoSpinner from "@/components/shared/LogoSpinner";
import { isAllowed, subscribeConsent } from "@/lib/consent";
import { BLOG_ROUTES } from "./blogRoutes";

// 5.4 provider scope refactor: Sonner + TooltipProvider lazy-loaded.
// Both unused on Index (home eager-loaded). Saves ~8 KB main chunk by deferring
// sonner (~5 KB gz) + @radix-ui/react-tooltip (~3 KB gz) to first non-home route
// hit. TooltipProvider is mounted via SiteProviders shell that wraps Routes; its
// Suspense boundary is shared with the existing route-level boundary.
const SiteProviders = lazy(() => import("./components/shared/SiteProviders.tsx"));

const Funzionalita = lazy(() => import("./pages/Funzionalita.tsx"));
const FunzionalitaCliente = lazy(() => import("./pages/FunzionalitaCliente.tsx"));
const FunzionalitaFiduciario = lazy(() => import("./pages/FunzionalitaFiduciario.tsx"));
const Fiduciari = lazy(() => import("./pages/Fiduciari.tsx"));
const Prezzi = lazy(() => import("./pages/Prezzi.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));
const Registrazione = lazy(() => import("./pages/Registrazione.tsx"));
const Termini = lazy(() => import("./pages/Termini.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Contatto = lazy(() => import("./pages/Contatto.tsx"));
const FAQ = lazy(() => import("./pages/FAQ.tsx"));
const Simulatore = lazy(() => import("./pages/Simulatore.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const VsFiduciarioTradizionale = lazy(() => import("./pages/VsFiduciarioTradizionale.tsx"));
const ConfrontoCantoni = lazy(() => import("./pages/ConfrontoCantoni.tsx"));
const Frontalieri = lazy(() => import("./pages/Frontalieri.tsx"));
const Grazie = lazy(() => import("./pages/Grazie.tsx"));
const ConfermaEmail = lazy(() => import("./pages/ConfermaEmail.tsx"));
const PressKit = lazy(() => import("./pages/PressKit.tsx"));
const PartnerFiduciari = lazy(() => import("./pages/PartnerFiduciari.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const CookieBanner = lazy(() => import("@/components/shared/CookieBanner"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <LogoSpinner size={56} />
    </div>
  );
}

function AnalyticsGate() {
  // M7: Vercel Analytics/SpeedInsights gated behind analytics consent.
  // nLPD/GDPR: even cookieless trackers need consent in some Swiss cantons.
  const [allow, setAllow] = useState(() => isAllowed("analytics"));
  useEffect(() => subscribeConsent((s) => setAllow(s.analytics)), []);
  if (!allow) return null;
  return (<><SpeedInsights /><Analytics /></>);
}

function App() {
  return (
  <ErrorBoundary>
    <I18nProvider>
      <Suspense fallback={null}>
        <SiteProviders>
        <LazyMotion features={domAnimation} strict>
        <AnalyticsGate />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/funzionalita" element={<Funzionalita />} />
              <Route path="/funzionalita-cliente" element={<FunzionalitaCliente />} />
              <Route path="/funzionalita-fiduciario" element={<FunzionalitaFiduciario />} />
              <Route path="/fiduciari" element={<Fiduciari />} />
              <Route path="/prezzi" element={<Prezzi />} />
              <Route path="/login" element={<Login />} />
              <Route path="/registrazione" element={<Registrazione />} />
              <Route path="/termini" element={<Termini />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contatto" element={<Contatto />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/simulatore" element={<Simulatore />} />
              <Route path="/blog" element={<Blog />} />
              {BLOG_ROUTES.map(({ slug, Component }) => (
                <Route key={slug} path={slug} element={<Component />} />
              ))}
              <Route path="/vs-fiduciario-tradizionale" element={<VsFiduciarioTradizionale />} />
              <Route path="/confronta-cantoni" element={<ConfrontoCantoni />} />
              <Route path="/frontalieri" element={<Frontalieri />} />
              <Route path="/grazie" element={<Grazie />} />
              <Route path="/conferma-email" element={<ConfermaEmail />} />
              <Route path="/press" element={<PressKit />} />
              <Route path="/partner-fiduciari" element={<PartnerFiduciari />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <CookieBanner />
          </Suspense>
        </BrowserRouter>
        </LazyMotion>
        </SiteProviders>
      </Suspense>
    </I18nProvider>
  </ErrorBoundary>
  );
}

export default App;
